import { useMemo } from "react";
import type { OneNoteTagGroupId } from "./OneNoteSourceSettings.types";

export interface OneNoteSourceSettingsText {
  locationLabelText: string;
  oneDriveLabelText: string;
  sharePointLabelText: string;
  groupLabelText: string;
  groupHintText: string;
  siteLabelText: string;
  siteHintText: string;
  siteSearchPlaceholderText: string;
  siteSearchMinCharsText: string;
  siteUrlLabelText: string;
  notebookLabelText: string;
  notebookHintText: string;
  notebookPlaceholderText: string;
  sectionGroupLabelText: string;
  sectionGroupHintText: string;
  sectionGroupPlaceholderText: string;
  sectionsLabelText: string;
  sectionsHintText: string;
  sectionsPlaceholderText: string;
  tagsLabelText: string;
  tagsHintText: string;
  tagsSelectAllText: string;
  tagsClearText: string;
  /** Display name of a tag group. */
  getTagGroupLabelText: (groupId: OneNoteTagGroupId) => string;
  /** Display name of a OneNote tag, by its `data-tag` value. */
  getTagLabelText: (tag: string) => string;
  filterStringLabelText: string;
  filterStringHintText: string;
  processingOptionsLabelText: string;
  includeBulletedListsText: string;
  includeBulletedListsHintText: string;
  includeTablesText: string;
  processingWarningText: string;
  dontProcessHintText: string;
  loadingText: string;
  loadErrorText: string;
  siteSearchFailedText: string;
  newConnectionMessageText: string;
  notSetText: string;
}

export const useOneNoteSourceSettingsText = (
  t: (key: string) => string,
): OneNoteSourceSettingsText => {
  return useMemo(() => {
    const tKey = (key: string): string =>
      t(`board.connections.dialog.sourceSettings.onenote.${key}`);

    return {
      locationLabelText: tKey("locationLabel"),
      oneDriveLabelText: tKey("oneDriveLabel"),
      sharePointLabelText: tKey("sharePointLabel"),
      groupLabelText: tKey("groupLabel"),
      groupHintText: tKey("groupHint"),
      siteLabelText: tKey("siteLabel"),
      siteHintText: tKey("siteHint"),
      siteSearchPlaceholderText: tKey("siteSearchPlaceholder"),
      siteSearchMinCharsText: tKey("siteSearchMinChars"),
      siteUrlLabelText: tKey("siteUrlLabel"),
      notebookLabelText: tKey("notebookLabel"),
      notebookHintText: tKey("notebookHint"),
      notebookPlaceholderText: tKey("notebookPlaceholder"),
      sectionGroupLabelText: tKey("sectionGroupLabel"),
      sectionGroupHintText: tKey("sectionGroupHint"),
      sectionGroupPlaceholderText: tKey("sectionGroupPlaceholder"),
      sectionsLabelText: tKey("sectionsLabel"),
      sectionsHintText: tKey("sectionsHint"),
      sectionsPlaceholderText: tKey("sectionsPlaceholder"),
      tagsLabelText: tKey("tagsLabel"),
      tagsHintText: tKey("tagsHint"),
      tagsSelectAllText: tKey("tagsSelectAll"),
      tagsClearText: tKey("tagsClear"),
      getTagGroupLabelText: (groupId: OneNoteTagGroupId) => tKey(`tagGroups.${groupId}`),
      getTagLabelText: (tag: string) => tKey(`tagNames.${tag}`),
      filterStringLabelText: tKey("filterStringLabel"),
      filterStringHintText: tKey("filterStringHint"),
      processingOptionsLabelText: tKey("processingOptionsLabel"),
      includeBulletedListsText: tKey("includeBulletedLists"),
      includeBulletedListsHintText: tKey("includeBulletedListsHint"),
      includeTablesText: tKey("includeTables"),
      processingWarningText: tKey("processingWarning"),
      dontProcessHintText: tKey("dontProcessHint"),
      loadingText: tKey("loading"),
      loadErrorText: tKey("loadError"),
      siteSearchFailedText: tKey("siteSearchFailed"),
      newConnectionMessageText: tKey("newConnectionMessage"),
      notSetText: tKey("notSet"),
    };
  }, [t]);
};
